import { useMemo } from 'react';
import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'FrontRoyalReactHookForm';
import { streamEditorPath } from 'Lessons';
import {
    OpenInNewTabButton,
    RemoveButton,
    ReorderButtons,
    preventBackspaceRemoval,
    renderOptionKeyedById,
} from 'ContentItemEditor';
import { courseTitle } from './useEditorCourses';
import { type EditorCourseOption, type PlaylistFormValues } from './PlaylistEditor.types';

type Props = {
    courses: EditorCourseOption[];
    coursesByLocalePackId: Record<string, EditorCourseOption>;
    coursesLoading: boolean;
    assessmentEditingLocked: boolean;
};

// The row order is the course order the learner sees, so the reorder buttons write straight to
// the field array.
export default function PlaylistCoursesSection({
    courses,
    coursesByLocalePackId,
    coursesLoading,
    assessmentEditingLocked,
}: Props) {
    const { control, watch } = useFormContext<PlaylistFormValues>();
    const { fields, append, remove, move } = useFieldArray({
        control,
        name: 'stream_locale_pack_ids',
    });

    // The ids come from the form values, not from `fields`. See FormStreamEntry. `fields` is
    // still what we map over, because its keys are stable across a reorder.
    const rows = watch('stream_locale_pack_ids');

    // A course already on the playlist is not worth offering again.
    const options = useMemo(() => {
        const chosen = new Set(rows.map(row => row.locale_pack_id));
        return courses.filter(course => !chosen.has(course.localePackId));
    }, [courses, rows]);

    return (
        <Stack spacing={1}>
            {assessmentEditingLocked && (
                <Typography variant="body2">
                    The ability to add/remove exams or streams containing SMARTCASEs has been disabled because
                    assessment editing has been locked.
                </Typography>
            )}

            <Autocomplete
                // The picker holds no value of its own: choosing a course appends it below.
                value={null}
                blurOnSelect
                loading={coursesLoading}
                options={options}
                getOptionLabel={course => courseTitle(course)}
                isOptionEqualToValue={(option, chosen) => option.localePackId === chosen.localePackId}
                onChange={(_event, course) => course && append({ locale_pack_id: course.localePackId })}
                onKeyDown={preventBackspaceRemoval}
                renderOption={renderOptionKeyedById(courseTitle)}
                renderInput={props => <TextField {...props} name="add_course" label="Add a Course" />}
            />

            {fields.length === 0 && <Typography variant="body2">No Courses Associated</Typography>}

            {fields.map((field, index) => {
                const localePackId = rows[index].locale_pack_id;
                const course = coursesByLocalePackId[localePackId];

                return (
                    <Stack key={field.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography sx={{ flexGrow: 1 }}>{courseTitle(course)}</Typography>

                        <Stack direction="row" sx={{ alignItems: 'center' }}>
                            <OpenInNewTabButton
                                href={course ? streamEditorPath({ streamId: course.id }) : ''}
                                label="Open this course in the editor"
                                disabled={!course}
                            />

                            <ReorderButtons index={index} count={fields.length} label="this course" onMove={move} />

                            <RemoveButton
                                name="remove_course"
                                label="Remove this course"
                                onClick={() => remove(index)}
                            />
                        </Stack>
                    </Stack>
                );
            })}
        </Stack>
    );
}
